import React, {useCallback, useEffect, useRef, useState} from 'react'
// import DaysDropdown from '../generic-table/days_dropdown'
import DatePicker from '../GenericTable/CommonDatePicker'
import GenericTable from '../GenericTable/table'
import Swal from 'sweetalert2'
import SkeletonCard from '../Auth/Components/SkeletonCard'
import { fetchScheduleCalls, scheduledCallsData } from './helper'
import { updateScheduleCallStatus } from '../../../api'

type SortConfig = {key: string; direction: 'asc' | 'desc' | null}

function ScheduledCalls() {
  const [columns, setColumns] = useState<string[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [selectedDays, setSelectedDays] = useState(() => {
    const currentDate = new Date();
    const endDate = currentDate.toISOString().split('T')[0];
    
    currentDate.setDate(currentDate.getDate() - 30);
    const startDate = currentDate.toISOString().split('T')[0];
  
    return {
      startDate,
      endDate,
    };
  });
  const [currentPage, setCurrentPage] = useState(1)
  const [currentPageData, setCurrentPageData] = useState<Record<string, any>[]>([])
  const [totalPages, setTotalPages] = useState<number>(1)
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: '', direction: null})
  const itemsPerPage = 10

  const handleDaysChange = (newDays) => {
    setSelectedDays(newDays);
  };
  const fetchData = useCallback(async () => {

    if (!selectedDays.startDate || !selectedDays.endDate) {
      setData([]);
      setLoading(false);
      return;
    }
  
    try {
      const data = await fetchScheduleCalls(selectedDays.startDate, selectedDays.endDate);
      setLoading(false);
  
      const rawColumns = scheduledCallsData.columns;
      const rawData = data;
  
      const mappedData = mapDataToColumns(rawData, rawColumns);
      const sortedData = sortData(mappedData, rawColumns[0]);
  
      setColumns(rawColumns);
      setData(sortedData);
      setTotalPages(Math.ceil(sortedData.length / itemsPerPage));
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching schedule calls data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDays, scheduledCallsData.columns, itemsPerPage]);

  const isFetching = useRef(false);   
  
  useEffect(() => {
    if (selectedDays.startDate && selectedDays.endDate && !isFetching.current) {
      isFetching.current = true;
      fetchData().finally(() => {
        isFetching.current = false;
      });
    }
  }, [selectedDays, fetchData]);
  
   const mapDataToColumns = (data, columns) => {
    if (!Array.isArray(data)) {
      console.error("mapDataToColumns: data is not an array", data)
      return []
    }
    return data.map((row) => {
      const mappedRow = {};
      columns.forEach((col) => {
        mappedRow[col] = row[col] || ''
      })
      return mappedRow
    })
  }

  const sortData = (data: Record<string, any>[], column: string) => {
    const sortableData = [...data]
    sortableData.sort((a, b) => {
      const aValue = a[column]
      const bValue = b[column]

      const aDate = Date.parse(aValue)
      const bDate = Date.parse(bValue)
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate
      }

      if (!isNaN(aValue) && !isNaN(bValue)) {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return 0
    })

    return sortableData
  }

  const handleSort = (column: string) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig.key === column && prevConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key: column, direction };
    });
  };
  
  useEffect(() => {
    if (sortConfig.key) {
      const sortedData = sortData(data, sortConfig.key);
      setData(sortedData);
    }
  }, [sortConfig]);

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  const handleStatusChange = async (Id: number, newStatus: string) => {
    const selectedRow = data.find((row) => row['Id'] === Id)

    if (!selectedRow) {
      Swal.fire('Error', 'ID not found.', 'error')
      return
    }
    Swal.fire({
      title: 'Are you sure you want to change the status?',
      // text: `Are you sure you want to change the status to ${newStatus}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await updateScheduleCallStatus({
            schedule_call_id: selectedRow['Id'],
            new_status: newStatus,
          })

          const updatedData = [...data]
          updatedData.forEach((row) => {
            if (row['Id'] === Id) {
              row['Status'] = newStatus
            }
          })
          setData(updatedData)
          Swal.fire('Updated!', response.message, 'success')
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'There was an issue updating the status.';
          Swal.fire('Error', errorMessage, 'error');
        }
      } else {
        Swal.fire('Cancelled', 'The status change has been discarded.', 'info')
      }
    })
  }

  useEffect(() => {
    const filteredData = data.filter((row) => {
      const matchesSearch = Object.values(row)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())

      return matchesSearch
    })
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage))

    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentPageData = filteredData.slice(startIndex, endIndex)

    setCurrentPageData(currentPageData)
  }, [data, currentPage, searchTerm])

  return (
    <div>
      {loading ? (
        <SkeletonCard />
      ) : (
        <>
          <GenericTable
            columns={columns}
            handleNextClick={handleNextClick}
            handlePriviousClick={handlePreviousClick}
            data={currentPageData}
            searchTerm={searchTerm}
            handleSort={handleSort}
            sortConfig={sortConfig}
            onSearch={(term) => {
              setSearchTerm(term)
              setCurrentPage(1) // Reset page to 1 on search
            }}
            renderCell={(column, row, rowIndex) => {
              if (column === 'Status') {
                return (
                  <select
                    value={row['Status']}
                    className='form-select form-select-solid w-150px'
                    data-row-index={rowIndex}
                    onChange={(e) => handleStatusChange(row['Id'], e.target.value)}
                  >
                    <option value='Pending'>Pending</option>
                    <option value='Done'>Done</option>
                  </select>
                )
              }
                
              if (column === 'Message') {
                const text = row[column];
                const maxLength = 20; // Set max length for display
            
                return (
                  <span
                  title={text} // Tooltip shows full text on hover
                  className='d-inline-block text-truncate'
                  style={{
                    maxWidth: '150px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    borderRadius: '8px', // Rounded corners applied
                    padding: '4px',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                >
                  {text.length > maxLength ? text.substring(0, maxLength) + '...' : text}
                </span>
                );
              }
              return row[column]
            }}
            // conditionalClass='card card-flush mb-6 mb-xl-9 mt-5'
            RightSideElement={<DatePicker setSelectedDays={handleDaysChange}/>}
          />

          <ul className='pagination justify-content-center' style={{margin: '15px 0'}}>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handlePreviousClick) handlePreviousClick()
                }}
                disabled={currentPage === 1}
                aria-label='Previous'
              >
                &laquo; Previous
              </button>
            </li>

            <li className='page-item active'>
              <div className='d-flex align-items-center'>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {currentPage}
                </button>
                <span className='mx-2'>of</span>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {totalPages}
                </button>
              </div>
            </li>

            <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handleNextClick) handleNextClick()
                }}
                disabled={currentPage >= totalPages}
                aria-label='Next'
              >
                Next &raquo;
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  )
}

export default ScheduledCalls